/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { routes } from '@/route-paths';
import { cn } from '@/lib/utils';

export interface AuthTabsProps {
    activeTab: 'login' | 'signup';
}

const tabClass = (isActive: boolean) =>
    cn(
        'pb-3 -mb-px border-b-2 text-sm font-bold uppercase tracking-[0.12em] transition-colors',
        isActive
            ? 'border-foreground text-foreground'
            : 'border-transparent text-muted-foreground hover:text-foreground'
    );

/** Login / Create Account tab links shared by the /login and /signup pages. */
export function AuthTabs({ activeTab }: AuthTabsProps) {
    const { t } = useTranslation('login');

    return (
        <div className="flex gap-8 border-b border-muted-foreground/10">
            <Link to={routes.login} className={tabClass(activeTab === 'login')} aria-current={activeTab === 'login'}>
                {t('tabs.login')}
            </Link>
            <Link to={routes.signup} className={tabClass(activeTab === 'signup')} aria-current={activeTab === 'signup'}>
                {t('tabs.createAccount')}
            </Link>
        </div>
    );
}
